import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import ActionsAdDiscardButton, {TYPES as actionAdDiscardButtonTypes} from './actions-ad-discard-button.js'

const NO_OP = () => {}

const ActionsAdDiscard = ({adId, isFavorite, onAfterDiscard, onDiscardError = NO_OP, origin = '', size, type}) => {
  const {domain} = useContext(Context)
  const config = domain.get('config')
  const {USER_NOT_LOGGED_IN_USER_ERROR} = config.get('ERRORS')
  const {LOGIN_SOURCE_ID_DISCARD_AD} = config.get('login')
  const {TRACKING_EVENT_NAMES, HIT_ORIGINS} = config.get('tracking')
  const {triggerLoginModal} = useContext(LoginModalContext)
  const {track} = useAnalyticsTrack()
  const delayToNextFrame = useDelayToNextFrame()

  const callDiscardUseCase = () => domain.get('add_user_discarded_ads_use_case').execute({adId})
  const trackUnfavoritedAd = async () => {
    const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.AD_UNFAVORITED
    })
    track(trackingData)
  }

  const removeFromFavoriteAndRunCallback = async () => {
    if (isFavorite) {
      const [, deleteFavoriteUseCaseResponse] = await domain.get('delete_user_favorites_use_case').execute({adId})
      deleteFavoriteUseCaseResponse && trackUnfavoritedAd()
    }

    trackDiscardSuccess()
    onAfterDiscard()
  }

  const trackDiscardClick = async () => {
    const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.AD_DISCARDED_CLICK,
      origin
    })
    track(trackingData)
  }

  const trackDiscardSuccess = async () => {
    const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.AD_DISCARDED,
      origin
    })
    track(trackingData)
  }

  const onClick = e => {
    e.stopPropagation()
    e.preventDefault()

    callDiscardUseCase()
      .then(async ([useCaseError, useCaseResponse]) => {
        const userIsNotLoggedIn = useCaseError instanceof USER_NOT_LOGGED_IN_USER_ERROR

        userIsNotLoggedIn &&
          triggerLoginModal({
            modalProps: {
              source: LOGIN_SOURCE_ID_DISCARD_AD,
              trackingOrigin: HIT_ORIGINS.AD_DISCARDED,
              onLogin: () => {
                callDiscardUseCase().then(removeFromFavoriteAndRunCallback)
              }
            }
          })

        if (useCaseResponse) {
          removeFromFavoriteAndRunCallback()
        }
      })
      .catch(onDiscardError)

    delayToNextFrame(trackDiscardClick)
  }

  return <ActionsAdDiscardButton onClick={onClick} size={size} type={type} />
}

ActionsAdDiscard.displayName = 'ActionsAdDiscard'
ActionsAdDiscard.propTypes = {
  adId: PropTypes.number.isRequired,
  isFavorite: PropTypes.string,
  onAfterDiscard: PropTypes.func,
  onDiscardError: PropTypes.func,
  origin: PropTypes.string,
  size: PropTypes.string,
  type: PropTypes.oneOf(Object.values(actionAdDiscardButtonTypes))
}

export default ActionsAdDiscard
export {actionAdDiscardButtonTypes as actionsAdDiscardTypes}
